const R = require('ramda');
const { failProofAssign, uniq } = require('../utils');
const { metricUtils } = require('../utils/index.js');
const utils = require('../utils/utils');

/** @type {DimensionElement} */
class DimensionElement {
  constructor(obj) {
    // super(obj);
    this.updateData(obj);
  }

  updateData(obj) {
    failProofAssign(this, obj || {});
    if (!this.campaignIds) this.campaignIds = [];
  }

  get dimensionId() {
    return this.architecture.dimensionIdByDimensionElementId[this.id];
  }

  get dimension() {
    return this.architecture.dimensionById[this.dimensionId];
  }

  get level() {
    if (this.dimension.isCustom) {
      return 'customDimension';
    } else {
      return this.dimension.id;
    }
  }

  addCampaignId(campaignId) {
    this.campaignIds.push(campaignId);
    if (!this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaignId]) {
      this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaignId] = {};
    }
    const dimensionElementIdByDimensionId = this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaignId];
    dimensionElementIdByDimensionId[this.dimensionId] = this.id;
  }

  addCampaignIds(campaignIds) {
    campaignIds.forEach(campaignId => this.addCampaignId(campaignId));
  }

  cleanCampaignIds() {
    (this.campaignIds || []).forEach(campaignId => {
      if (this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaignId]) {
        delete this.architecture.dimensionElementIdByDimensionIdByCampaignId[campaignId][this.dimensionId];
      }
    });
    this.campaignIds = [];
  }

  updateCampaignIds(campaignIds) {
    this.cleanCampaignIds();
    this.addCampaignIds(campaignIds);
    this.campaignIds = campaignIds;
  }

  get campaigns() {
    return this.getCampaignsFromIds();
  }

  getReports({ dateGroup, detail } = {}) {
    // for dimensions data we need to merge the campaigns report
    let reports = [];
    const campaignReports = this.campaigns.flatMap(campaign => campaign.getReports({ dateGroup, detail }));
    if (dateGroup) {
      reports = utils.mergeReportArrayByDate(campaignReports);
    } else {
      reports = [
        {
          level: this.level,
          projectId: this.id,
          ...utils.mergeReportArray(campaignReports),
        },
      ];
    }
    return reports;
  }

  getCampaignsFromIds(campaignIds = this.campaignIds) {
    return campaignIds
      .map(id => {
        const { channel, channelId } = utils.getInfoFromId(id);
        let campaignId = `${channel}:campaign:${channelId}`;
        return this.architecture.campaignsById[campaignId];
      })
      .filter(Boolean);
  }

  get metric() {
    return this.getMetric();
  }

  getMetric(campaignIds = this.campaignIds) {
    const campaigns = this.getCampaignsFromIds(campaignIds);
    const metricAndConversionList = campaigns.map(campaign => ({
      metric: campaign.metric,
      conversionsByTracker: campaign.getConversionsByTracker(),
    }));
    const { metric } = metricUtils.mergeMetricAndConversion(metricAndConversionList);
    return metric;
    // let metric = {};
    // const reports = this.getReports();
    // const report = reports[0];
    // if (report && report.metric) {
    //     metric = metricUtils.getMetricFromReport(report, this);
    // } else {
    //     return null;
    // }
    // return metric;
  }

  /**
   * group all conversion by type of tracker
   * search children
   * @returns {{ga:[], pixel:[], lead:[]}}
   */
  getConversionsByTracker() {
    let reports = this.getReports();
    if (reports && !R.isEmpty(reports)) {
      let report = reports[0];
      return utils.getConversionsByTracker(report.actions);
    } else return [];
  }

  mergeCampaignData(property) {
    return uniq(this.campaigns.flatMap(campaign => campaign[property]).filter(value => !R.isEmpty(value)));
  }

  get subChannels() {
    return this.mergeCampaignData('subChannels');
  }

  get objectives() {
    return this.mergeCampaignData('objectives');
  }

  get placements() {
    return this.mergeCampaignData('placements');
  }

  getConversionAndMetric({ dateGroup, detail } = {}) {
    const conversionAndMetricPerCampaign = this.campaigns
      .flatMap(campaign => campaign.getConversionAndMetric({ dateGroup, detail }))
      .filter(Boolean);
    if (dateGroup) {
      // group conversion and metric by the from date
      const conversionAndMetricByDate = conversionAndMetricPerCampaign.reduce((acc, conversionAndMetric) => {
        if (!acc[conversionAndMetric.from]) acc[conversionAndMetric.from] = [];
        acc[conversionAndMetric.from].push(conversionAndMetric);
        return acc;
      }, {});
      // for each from date merge the conversion and the metric
      return Object.values(conversionAndMetricByDate).map(conversionAndMetricList => {
        return metricUtils.mergeMetricAndConversion(conversionAndMetricList);
      });
    } else {
      return metricUtils.mergeMetricAndConversion(conversionAndMetricPerCampaign);
    }
  }

  format({ campaignIds = this.campaignIds, dateGroup, parentElementTableId = null } = {}) {
    const campaigns = this.getCampaignsFromIds(campaignIds);
    const obj = {
      ...R.pick(['id', 'name', 'level'], this),
      dimension: R.pick(['id', 'name'], this.dimension),
      nbCampaigns: campaignIds.length,
    };

    // for the graph we need the metric per date
    if (dateGroup) {
      obj.conversionsAndMetricPerDate = this.getConversionAndMetric({
        dateGroup,
      });
    } else {
      obj.cell = metricUtils.getMergedCellFromCampaigns({
        campaigns,
        availableMetrics: this.architecture.availableMetrics,
      });
    }
    if (!obj.name) {
      obj.name = this.title;
    }
    // add tableId only for the FE
    obj.tableId = obj.id;
    if (parentElementTableId) {
      obj.tableId += '-' + parentElementTableId;
    }

    obj.kpi = this.architecture.getKPI({ id: obj.tableId, level: obj.level });
    return obj;
  }

  getJSON() {
    const channelCampaignIds = this.campaigns.map(({ id }) => id);
    return {
      ...R.omit(['architecture', 'FEFormat'], this),
      metric: this.metric,
      channelCampaignIds,
      schedule: this.getSchedule(),
    };
  }
}

module.exports = DimensionElement;
